import React, { Fragment, useCallback, useEffect, useState, useRef } from 'react'
import { message, Spin } from 'antd'
import { useForm } from 'antd/lib/form/Form'
import { useAppContext } from '@/components/AppContext'
import TempleCreation from '../components/StoreForm'
import StoreCloneModal from '@a4b/ui/src/modules/monetisation/puja/molecules/StoreCloneModal'
import { createSearchParams, useSearchParams } from 'react-router-dom'
import { formatCreateTempleApi, parseCreateTempleApiEdit } from '../utils/helper'
import { debounce } from 'lodash'
import usePujaStore from '../../../stores/puja-store'
import ConfirmationPopup from '../components/ConfirmationPopup'
import { CustomLoader } from '@/components/Loader'
import { openErrorNotification } from '@/utils'
import { getMonetisationPujaApi, showErrorToast } from '@/utils/helper'
import { fetchSearchMetadata } from '@/modules/monetisation/search/utils/helper'

interface Props {
  isEditJourney: boolean
  currentStep: number
  storeId: string
  nextHandler: (store_id?: string) => void
  previousHandler: () => void
}

const TempleCreationStep: React.FC<Props> = ({
  currentStep,
  isEditJourney,
  storeId,
  previousHandler,
  nextHandler,
}) => {
  const { networkInstance, countryState, setLanguageContext, setCountryState, app, languageState } = useAppContext()
  const { experiment, variant } = usePujaStore()
  const monetisationPujaApi = getMonetisationPujaApi(app, networkInstance)
  const [templeFormInstance] = useForm()
  const [searchParams, setSearchParams] = useSearchParams()

  const [slugState, setSlugState] = useState<boolean>(false)
  const [storeData, setStoreData] = useState<any>()
  const [initialStoreData, setInitialStoreData] = useState<any>()
  const [showConfirmation, setShowConfirmation] = useState<boolean>(false)
  const [showCloneModal, setShowCloneModal] = useState<boolean>(false)
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [isGenieLoading, setIsGenieLoading] = useState<boolean>(false)
  const [cloneFormInstance] = useForm()
  const { setExperiment, setVariant } = usePujaStore()

  // Ref to store the genie API promise
  const geniePromiseRef = useRef<Promise<any> | null>(null)
  const pendingStoreDataRef = useRef<any>(null)

  const fetchStoreData = useCallback(async () => {
    try {
      setIsLoading(true);
      if (!experiment && variant) return
      if (storeId) {
        const storeResponse = await monetisationPujaApi.storesApi.getStoreById(storeId, countryState?.countryCode, undefined, experiment, variant);
        const parsedData = parseCreateTempleApiEdit(storeResponse?.data?.data, languageState);

        setSearchParams(
          createSearchParams({
            service_type: parsedData?.service_type as any,
            edit: searchParams.get('edit') || '',
            store_id: searchParams.get("store_id") || "",
            language_code: languageState?.monetisationLanguage,
            active: storeResponse?.data?.data?.active ? "true" : "false",
            country_code: countryState?.countryCode
          })
        );

        templeFormInstance.setFieldsValue(parsedData);
        setInitialStoreData(parsedData);
      }
    } catch (error) {
      showErrorToast(error)
      //message.error('Error while fetching store data', 3);
      console.log(error);
    } finally {
      setTimeout(() => {
        setIsLoading(false);
      }, 300)
    }
  }, [monetisationPujaApi.storesApi, storeId, countryState?.countryCode, templeFormInstance, experiment, variant]);

  useEffect(() => {
    if (isEditJourney) {
      fetchStoreData();
    }
  }, [isEditJourney, fetchStoreData]);

  useEffect(() => {
    !isEditJourney && templeFormInstance.setFieldsValue({
      service_type: 'pooja',
      temple_media: {
        media_type: 'image'
      }
    })
  }, [countryState?.countryCode, isEditJourney, searchParams, templeFormInstance])

  const handleStoreCreateOrUpdate = async () => {
    if (isEditJourney) {
      try {
        if (!slugState) {
          // Wait for genie API to complete if it's running
          if (geniePromiseRef.current) {
            setIsGenieLoading(true)
            await geniePromiseRef.current
            setIsGenieLoading(false)
          }

          await monetisationPujaApi.storesApi.updateStore(
            pendingStoreDataRef.current || storeData,
            storeId,
            experiment,
            variant
          )

          message.success({ content: 'Temple Updated Successfully', duration: 2 })
          nextHandler()

        } else {
          message.error({ content: 'Please Enter Valid slug', duration: 2 })
        }
      } catch (error: any) {
        setIsGenieLoading(false)
        // This executes for network errors or thrown exceptions
        showErrorToast(error)
        //const errorMessage = error?.response?.data?.error || error.message || 'Unknown error'
        //message.error(`Error while updating temple: ${errorMessage?.message}`, 3)
      }
    } else {
      try {
        if (!slugState) {
          // Wait for genie API to complete if it's running
          if (geniePromiseRef.current) {
            setIsGenieLoading(true)
            await geniePromiseRef.current
            setIsGenieLoading(false)
          }

          const finalStoreData = pendingStoreDataRef.current || storeData
          const createResponse = await monetisationPujaApi.storesApi.createStore(
            finalStoreData,
          )
          message.success({ content: 'Temple Created Successfully', duration: 2 })
          setSearchParams(
            createSearchParams({
              edit: searchParams.get('edit') || "true",
              store_id: createResponse?.data?.data?.store_id,
              current_step: currentStep.toString(),
              service_type: finalStoreData?.service_type,
            }),
          )
          setLanguageContext({ ...languageState, isLanguageSelectionActive: false })
          setCountryState({ ...countryState, isCountryCodeActive: false })
          nextHandler(createResponse?.data?.data?.store_id)
        } else {
          message.error({ content: 'Please enter valid slug', duration: 2 })
        }
      } catch (error: any) {
        setIsGenieLoading(false)
        console.log(error)
        showErrorToast(error)
        //message.error('Error while creating temple' + error?.message, 3)
      }
    }
  }

  const handleOk = () => {
    setShowConfirmation(false)
    handleStoreCreateOrUpdate()
  }

  const handleCancel = () => {
    setShowConfirmation(false)
    // Clear the genie promise if user cancels
    geniePromiseRef.current = null
    pendingStoreDataRef.current = null
    setIsGenieLoading(false)
  }

  const handleCloneModal = useCallback(() => {
    setShowCloneModal((prevState) => (!prevState))
  }, [])

  const updateStoreByDestinationId = async (data: any, values: any) => {
    const storePayload = {
      ...data,
      language_code: values?.destination_language,
      country_code: values?.destination_country
    }
    try {
      await monetisationPujaApi.storesApi.updateStore(
        storePayload,
        storeId,
      )
      message.success({ content: `Temple Cloned Successfully to language:${values?.destination_language}, country: ${values?.destination_country} `, duration: 2 })
      setShowCloneModal((prevState) => (!prevState))
    } catch (error: any) {
      showErrorToast(error)
      //message.error('Error while cloning temple' + error?.message, 3)
    }
  }

  const getStoreBySourceId = async (values: any) => {
    const countryCode = values?.source_country
    const languageCode = values?.source_language
    try {
      const storeResponse = await monetisationPujaApi.storesApi.getStoreById(
        storeId,
        countryCode,
        languageCode
      )
      const storeData = storeResponse?.data?.data
      if (
        languageCode === storeData?.language_code &&
        countryCode === storeData?.country_code
      ) {
        updateStoreByDestinationId(storeResponse?.data?.data, values)
      } else {
        message.error(`No data available for language: ${languageCode}, country: ${countryCode}`, 3)
      }
    } catch (error) {
      showErrorToast(error)
      //message.error('Error while fetching store by source Id', 3)
    }
  }

  const handleCloneSubmit = () => {
    cloneFormInstance
      .validateFields()
      .then((values: any) => {
        getStoreBySourceId(values)
      })
  }

  // const callGenieApi = async (values: any, isTrueSet: boolean) => {
  //   try {
  //     const searchmetadata = await fetchSearchMetadata(networkInstance);
  //     const payload = {
  //       service_code: 'puja',
  //       store_data: {
  //         title: templeFormInstance.getFieldValue("title"),
  //         short_description: templeFormInstance.getFieldValue("short_description"),
  //         long_description: templeFormInstance.getFieldValue("long_description"),
  //         display_title: templeFormInstance.getFieldValue("puja_title") + templeFormInstance.getFieldValue("pooja_usecase"),
  //       },
  //       festival_enums: searchmetadata?.festivalOptions?.map(option => option?.name) || [],
  //       deity_enums: searchmetadata?.deities?.map(option => option?.value) || [],
  //       tithi_enums: searchmetadata?.thithiOptions?.map(option => option?.name) || []
  //     };

  //     const genieResponse = await networkInstance.clientWithHeaders.monetisationOfferingsApi.systemApi.getSystemGenieData(payload);
  //     const searchResData = {
  //       festival: genieResponse?.data?.data?.data?.model_response_data?.festivals?.length > 0 ?
  //         genieResponse?.data?.data?.data?.model_response_data?.festivals?.[0] : null,
  //       deity: genieResponse?.data?.data?.data?.model_response_data?.deities?.length > 0 ?
  //         genieResponse?.data?.data?.data?.model_response_data?.deities?.[0] : null,
  //       rashi: genieResponse?.data?.data?.data?.model_response_data?.rashis?.length > 0 ?
  //         genieResponse?.data?.data?.data?.model_response_data?.rashis?.[0] : null,
  //       tithi: genieResponse?.data?.data?.data?.model_response_data?.tithis?.length > 0 ?
  //         genieResponse?.data?.data?.data?.model_response_data?.tithis?.[0] : null,
  //     };

  //     // Only use genie data if isTrueSet is false
  //     if (!isTrueSet) {
  //       values.search_metadata.festival = null
  //       values.search_metadata.tithi = null
  //       values.search_metadata.rashi = null
  //       values.search_metadata.deity = null
  //     }

  //     // Update the store data with genie results
  //     let formData;
  //     if (isEditJourney) {
  //       formData = formatCreateTempleApi({
  //         ...values,
  //         position: templeFormInstance.getFieldValue("position"),
  //         id: storeId,
  //       }, languageState, isTrueSet, isEditJourney, countryState?.countryCode, searchResData);
  //     } else {
  //       formData = formatCreateTempleApi(
  //         values,
  //         languageState,
  //         isTrueSet,
  //         isEditJourney,
  //         countryState?.countryCode,
  //         searchResData
  //       );
  //     }

  //     const finalStoreData = { ...formData, country_code: countryState?.countryCode };
  //     pendingStoreDataRef.current = finalStoreData;

  //     return finalStoreData;
  //   } catch (error) {
  //     console.warn(error);
  //     // Return the original store data if genie fails
  //     return pendingStoreDataRef.current;
  //   }
  // }

  const handleTempleCreateOrUpdate = useCallback(async (values: any) => {
    const isTrueSet = (searchParams.get("active")?.toLowerCase() === 'true');

    // Check if we need to call genie API
    // const shouldCallGenie = (isEditJourney && !isTrueSet) || (
    //   !values?.search_metadata?.tithi &&
    //   !values?.search_metadata?.festival &&
    //   !values?.search_metadata?.rashi &&
    //   !values?.search_metadata?.deity
    // );

    searchParams.set("service_type", values?.service_type);
    searchParams.set("store_type", values?.store_type);
    searchParams.set("language_code", languageState?.monetisationLanguage);
    searchParams.set("country_code", countryState?.countryCode);
    setSearchParams(searchParams);

    // Prepare initial form data without genie results
    let formData;
    if (isEditJourney) {
      formData = formatCreateTempleApi({
        ...values,
        position: templeFormInstance.getFieldValue("position"),
        id: storeId,
      }, languageState, isTrueSet, isEditJourney, countryState?.countryCode, null);
    } else {
      formData = formatCreateTempleApi(
        values,
        languageState,
        isTrueSet,
        isEditJourney,
        countryState?.countryCode,
        null
      );
    }

    const initialStoreData = { ...formData, country_code: countryState?.countryCode };
    setStoreData(initialStoreData);
    pendingStoreDataRef.current = initialStoreData;

    // if (shouldCallGenie) {
    //   // Start genie API call but don't wait for it
    //   geniePromiseRef.current = callGenieApi(values, isTrueSet);
    // }

    // Show confirmation modal after 200ms
    // setTimeout(() => {
    setShowConfirmation(true);
    // }, 200);

  }, [countryState?.countryCode, isEditJourney, languageState, searchParams, setSearchParams, storeId, templeFormInstance]);

  const handleTempleCreate = useCallback(() => {
    templeFormInstance
      .validateFields()
      .then((values: any) => {
        handleTempleCreateOrUpdate(values)
      })
      .catch(error => {
        console.log('Temple Creation error')
      })
  }, [handleTempleCreateOrUpdate, templeFormInstance])

  const validateSlugField = async (slug: string): Promise<void> => {
    if (!slug) return;

    try {
      const slugResponse = await monetisationPujaApi.storesApi.getSlugDuplicate(slug);
      const isDuplicate = slugResponse?.data?.data?.is_duplicate;
      setSlugState(isDuplicate);
    } catch (error) {
      openErrorNotification(error)
      setSlugState(true);
    }
  }

  const debouncedHandler = debounce(validateSlugField, 1000)

  const onValuesChange = useCallback((values: any) => {
    debouncedHandler(values?.slug)
  }, [debouncedHandler])

  useEffect(() => {
    setExperiment(undefined)
    setVariant(undefined)
  }, [])

  const memoizedNextHandler = useCallback(() => {
    nextHandler();
  }, [nextHandler]);

  if (isLoading) {
    return <div style={{ height: '50vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <Spin indicator={<CustomLoader />} />
    </div>
  }

  return (
    <Fragment>
      <TempleCreation
        form={templeFormInstance}
        initialStoreData={initialStoreData}
        submitFormHandler={handleTempleCreate}
        isEditJourney={isEditJourney}
        currentStep={currentStep}
        previousHandler={previousHandler}
        nextHandler={memoizedNextHandler}
        onValuesChange={onValuesChange}
        slugState={slugState}
        handleCloneModal={handleCloneModal}
      />

      {storeData &&
        <ConfirmationPopup
          storeData={storeData}
          visible={showConfirmation}
          handleOk={handleOk}
          handleCancel={handleCancel}
          title={'Store'}
        />
      }
      {
        showCloneModal &&
        <StoreCloneModal
          form={cloneFormInstance}
          showCloneModal={showCloneModal}
          handleCloneModal={handleCloneModal}
          handleCloneSubmit={handleCloneSubmit}
        />
      }
    </Fragment>
  )
}

export default TempleCreationStep